/**
 * The entry (probationer) application ladder, in one place.
 *
 * The SOP is the firm-registration one:
 *
 *   pending          the applicant is still filling it in
 *   acknowledged     submitted and paid for — waiting on the CHAPTER and the
 *                    two REFEREES. Nobody at the institute acts on it here.
 *   under_review     endorsed all round; with the SECRETARIAT, who may return it
 *                    or hand it to the committee
 *   with_committee   with the Membership Committee, who decide it
 *   approved /       the committee's decision
 *   rejected
 *   returned         sent back to the applicant to correct
 *
 * `awaiting_endorsements` is the same rung as `acknowledged` under an older
 * name — there is no admin vetting step before the endorsements any more, so
 * nothing writes it. It is still recognised here so an old row reads correctly.
 */

export type ApplicationStatus =
  | "pending"
  | "acknowledged"
  | "awaiting_endorsements"
  | "under_review"
  | "with_committee"
  | "submitted"
  | "returned"
  | "approved"
  | "rejected";

/** What each rung is called on screen. */
export const STATUS_LABELS: Record<string, string> = {
  pending: "Draft",
  acknowledged: "Awaiting endorsements",
  awaiting_endorsements: "Awaiting endorsements",
  under_review: "Secretariat review",
  with_committee: "With committee",
  submitted: "Secretariat review",
  returned: "Returned",
  approved: "Approved",
  rejected: "Rejected",
};

/** One line saying where the application actually stands, for the applicant. */
export const STATUS_BLURBS: Record<string, string> = {
  pending: "Not submitted yet — finish the form and submit it.",
  acknowledged:
    "Submitted. Waiting for your chapter to endorse it and for both referees to accept.",
  awaiting_endorsements:
    "Submitted. Waiting for your chapter to endorse it and for both referees to accept.",
  under_review: "Endorsed. The secretariat is reviewing it.",
  submitted: "Endorsed. The secretariat is reviewing it.",
  with_committee: "With the Membership Committee, who decide it.",
  returned: "Returned to you for corrections — fix what was asked and submit again.",
  approved: "Approved by the Membership Committee.",
  rejected: "Not approved.",
};

export function statusLabel(status?: string | null): string {
  const key = String(status ?? "pending").toLowerCase();
  return STATUS_LABELS[key] ?? key.replace(/_/g, " ");
}

/** Tailwind classes per rung — one palette, so every screen reads the same. */
export function statusColors(status?: string | null) {
  switch (String(status ?? "").toLowerCase()) {
    case "approved":
      return {
        border: "border-green-200",
        active: "bg-green-600 text-white border-green-600",
        inactive: "bg-green-50 text-green-800 hover:bg-green-100",
        pill: "bg-green-100 text-green-800 border-green-200",
      };
    case "with_committee":
      return {
        border: "border-purple-200",
        active: "bg-purple-600 text-white border-purple-600",
        inactive: "bg-purple-50 text-purple-800 hover:bg-purple-100",
        pill: "bg-purple-100 text-purple-800 border-purple-200",
      };
    case "under_review":
    case "submitted":
      return {
        border: "border-blue-200",
        active: "bg-blue-600 text-white border-blue-600",
        inactive: "bg-blue-50 text-blue-800 hover:bg-blue-100",
        pill: "bg-blue-100 text-blue-800 border-blue-200",
      };
    case "acknowledged":
    case "awaiting_endorsements":
      return {
        border: "border-violet-200",
        active: "bg-violet-600 text-white border-violet-600",
        inactive: "bg-violet-50 text-violet-800 hover:bg-violet-100",
        pill: "bg-violet-100 text-violet-800 border-violet-200",
      };
    case "rejected":
      return {
        border: "border-red-200",
        active: "bg-red-600 text-white border-red-600",
        inactive: "bg-red-50 text-red-800 hover:bg-red-100",
        pill: "bg-red-100 text-red-800 border-red-200",
      };
    case "returned":
      return {
        border: "border-orange-200",
        active: "bg-orange-500 text-white border-orange-500",
        inactive: "bg-orange-50 text-orange-800 hover:bg-orange-100",
        pill: "bg-orange-100 text-orange-800 border-orange-200",
      };
    case "pending":
      return {
        border: "border-gray-300",
        active: "bg-gray-600 text-white border-gray-600",
        inactive: "bg-gray-50 text-gray-700 hover:bg-gray-100",
        pill: "bg-gray-100 text-gray-700 border-gray-300",
      };
    default:
      return {
        border: "border-black",
        active: "bg-black text-white border-black",
        inactive: "bg-white text-gray-800 hover:bg-gray-50",
        pill: "bg-gray-100 text-gray-800 border-gray-200",
      };
  }
}

/** Both names for the endorsement-collecting rung — old rows included. */
export const AWAITING_ENDORSEMENTS: string[] = [
  "acknowledged",
  "awaiting_endorsements",
];

/** Rungs the secretariat and the committee still hold. */
export const IN_REVIEW: string[] = [
  ...AWAITING_ENDORSEMENTS,
  "under_review",
  "with_committee",
  "submitted",
];

export const isAwaitingEndorsements = (status?: string | null) =>
  AWAITING_ENDORSEMENTS.includes(String(status ?? "").toLowerCase());

/* ══════════════════════════════════════════════════════════════════════════
   Listing the queue — tabs, endorsements, and how a row is filed

   Lifted out of AdminProbationerApplicationDietCategoryPage so the exports can
   file rows exactly as the screen does. The upgrade module keeps the same split
   for the same reason (utils/upgradeStatus.ts): a listing that disagrees with
   the screen it was taken from is worse than no listing.
   ══════════════════════════════════════════════════════════════════════════ */

export function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export type StatusKey =
  | "all"
  | "pending"
  | "acknowledged"
  | "under_review"
  | "with_committee"
  | "returned"
  | "approved"
  | "rejected";

/**
 * One tab per rung of the ladder, in the order an application climbs it.
 *
 * `pending` is this module's word for a draft. `acknowledged` is submitted and
 * paid, waiting on the chapter and referees — the older `awaiting_endorsements`
 * is the same rung, so it is counted under that tab rather than given its own,
 * and so is `submitted` under secretariat review.
 */
export const STATUS_TABS: { key: StatusKey; label: string; aliases?: string[] }[] = [
  { key: "all", label: "All" },
  { key: "pending", label: "Draft" },
  { key: "acknowledged", label: "Awaiting Endorsements", aliases: ["awaiting_endorsements"] },
  { key: "under_review", label: "Secretariat Review", aliases: ["submitted"] },
  { key: "with_committee", label: "With Committee" },
  { key: "returned", label: "Returned" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

/** Every status a tab stands for, itself included. */
export const tabStatuses = (key: StatusKey): string[] => {
  const tab = STATUS_TABS.find((t) => t.key === key);
  return [key, ...(tab?.aliases ?? [])];
};

export const tabLabel = (key: StatusKey): string =>
  STATUS_TABS.find((t) => t.key === key)?.label ?? "All";

/** Which tab a row is listed under — "all" for anything off the ladder. */
export function panelOf(r: any): StatusKey {
  const s = String(r?.status ?? "").toLowerCase();
  return STATUS_TABS.find((t) => t.key !== "all" && tabStatuses(t.key).includes(s))?.key ?? "all";
}

export function matchesStatusTab(r: any, tab: StatusKey): boolean {
  return tab === "all" || tabStatuses(tab).includes(String(r?.status ?? "").toLowerCase());
}

export function matchesQuery(r: any, query: string): boolean {
  const nq = query.trim().toLowerCase();
  if (!nq) return true;
  return [
    r?.id,
    r?.surname,
    r?.other_names,
    r?.user?.name,
    r?.user?.email,
    r?.email,
    r?.membership_no,
    r?.chapter?.name ?? r?.chapter_other,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(nq);
}

/**
 * Counted where each row is LISTED, not where its status column says —
 * otherwise a tab's pill promises rows the tab does not show.
 */
export function countsByTab(rows: any[]): Map<StatusKey, number> {
  const m = new Map<StatusKey, number>();
  for (const t of STATUS_TABS) m.set(t.key, 0);
  m.set("all", rows.length);
  for (const r of rows) {
    const key = panelOf(r);
    if (key !== "all") m.set(key, (m.get(key) ?? 0) + 1);
  }
  return m;
}

/* ── Endorsements ────────────────────────────────────────────────────────── */

export type EndorseState = "approved" | "rejected" | "pending" | "missing" | "not_recorded";

export const ENDORSE_LABELS: Record<EndorseState, string> = {
  approved: "Accepted",
  rejected: "Declined",
  pending: "Awaiting",
  missing: "Not named",
  not_recorded: "Not recorded",
};

/**
 * Is this application still actually waiting on its endorsements?
 *
 * Only while it sits on the rung that collects them. Once it has moved — to the
 * secretariat, the committee, or a decision — nobody is waiting on the chapter
 * or the referees any more, and showing "Awaiting" against a decided
 * application says the opposite of what is true. Imported and legacy
 * applications make this visible: they were admitted by the committee without
 * ever passing through the endorsement rung, so they carry no endorsements at
 * all and every row read as though a chapter still owed a response.
 */
export function stillCollectingEndorsements(r: any): boolean {
  const s = String(r?.status ?? "").toLowerCase();
  return ["pending", "acknowledged", "awaiting_endorsements", "returned"].includes(s);
}

/**
 * The chapter has no "declined" field — only `chapter_accepted_at` — so a
 * chapter is either endorsed, still owing a response, or (on an application
 * that has moved on without one) never recorded.
 */
export function chapterState(r: any): EndorseState {
  if (!r?.chapter_id && !r?.chapter_other) return "missing";
  if (r?.chapter_accepted_at) return "approved";
  return stillCollectingEndorsements(r) ? "pending" : "not_recorded";
}

/** The nth referee (0-based) with their decision, in a stable order. */
export function refereeCell(r: any, index: number): {
  name: string | null;
  state: EndorseState;
  on: string | null;
} {
  const ref = (r?.referees ?? [])[index];
  if (!ref) return { name: null, state: "missing", on: null };

  const m = ref.member;
  const name = m
    ? [m.surname, m.firstname].filter(Boolean).join(" ") || m.membership_no
    : ref.member_id ?? null;

  const decision = String(ref.decision ?? "").toLowerCase();
  const state: EndorseState =
    decision === "approved"
      ? "approved"
      : decision === "rejected"
        ? "rejected"
        : stillCollectingEndorsements(r)
          ? "pending"
          : "not_recorded";

  return { name, state, on: ref.approved_at ?? ref.rejected_at ?? null };
}

/** Chapter endorsed and both referees accepted — ready for the committee. */
export function isFullyEndorsed(r: any): boolean {
  const referees = (r?.referees ?? []).filter(
    (x: any) => String(x?.decision ?? "").toLowerCase() === "approved",
  ).length;
  return !!r?.chapter_accepted_at && referees >= 2;
}
